import React from 'react';
import { motion } from 'framer-motion';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { appShowcase } from '../data/portfolio';
import { ExternalLink, Github, Mic, Activity, FileText, Music, MessagesSquare } from 'lucide-react';
import ParticleField from './ParticleField';
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.12, duration: 0.5, ease: 'easeOut' }
  })
};

const iconMap = { mic: Mic, activity: Activity, 'file-text': FileText, music: Music, 'messages-square': MessagesSquare};

const badgeColorMap = {
  Live: 'bg-accent/10 text-accent border-accent/30',
  SaaS: 'bg-secondary/10 text-secondary border-secondary/30',
  Demo: 'bg-primary/10 text-primary border-primary/30'
};

const colorMap = {
  primary: {
    glow: 'hsl(var(--primary) / 0.15)',
    icon: 'hsl(var(--primary))',
    iconBg: 'hsl(var(--primary) / 0.1)',
    border: 'hsl(var(--primary) / 0.25)'
  },
  secondary: {
    glow: 'hsl(var(--secondary) / 0.12)',
    icon: 'hsl(var(--secondary))',
    iconBg: 'hsl(var(--secondary) / 0.1)',
    border: 'hsl(var(--secondary) / 0.25)'
  },
  accent: {
    glow: 'hsl(var(--accent) / 0.15)',
    icon: 'hsl(var(--accent))',
    iconBg: 'hsl(var(--accent) / 0.1)',
    border: 'hsl(var(--accent) / 0.25)'
  }
};

const AppCard = ({ app, index }) => {
  const Icon = iconMap[app.icon];
  const c = colorMap[app.color];

  return (
    <motion.div
      variants={fadeUp}
      initial="hidden"
      whileInView="visible"
      custom={index}
      viewport={{ once: true }}
      whileHover={{ y: -6, scale: 1.01 }}
      transition={{ duration: 0.3 }}
      className="glass glass-hover rounded-2xl p-6 flex flex-col relative overflow-hidden"
      style={{ '--hover-glow': c.glow }}
    >
      {/* Background accent */}
      <div
        className="absolute top-0 right-0 w-32 h-32 rounded-full pointer-events-none -translate-y-1/2 translate-x-1/2"
        style={{ background: `radial-gradient(circle, ${c.glow} 0%, transparent 70%)` }}
      />

      <div className="flex items-start justify-between mb-4">
        <div
          className="w-12 h-12 rounded-2xl flex items-center justify-center"
          style={{ background: c.iconBg, border: `1px solid ${c.border}` }}
        >
          <Icon size={22} style={{ color: c.icon }} />
        </div>
        <Badge variant="outline" className={`text-xs font-mono border ${badgeColorMap[app.badge]}`}>
          {app.badge}
        </Badge>
      </div>

      <div className="mb-3">
        <p className="text-muted-foreground font-mono text-xs mb-1">{app.type}</p>
        <h3 className="font-heading font-bold text-foreground text-lg">{app.title}</h3>
      </div>

      <p className="text-muted-foreground text-sm leading-relaxed mb-5 flex-1">
        {app.description}
      </p>

      <div className="flex flex-wrap gap-1.5 mb-5">
        {app.tech.map((t) => (
          <span
            key={t}
            className="font-mono text-xs px-2 py-0.5 rounded-md border border-border/60 text-muted-foreground"
          >
            {t}
          </span>
        ))}
      </div>

      <div className="flex gap-3 mt-auto">
        {app.liveUrl && app.liveUrl !== '#' ? (
          <Button
            size="sm"
            className="flex-1 font-medium "
            style={{ background: `${c.glow}`, border: `1px solid ${c.border}`, color: c.icon }}
            asChild
          >
            <a href={app.liveUrl} target="_blank" rel="noopener noreferrer">
              <ExternalLink size={13} className="mr-1.5" />
              Try it Live
            </a>
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="flex-1 border-border/60 text-muted-foreground cursor-not-allowed opacity-60"
            disabled
          >
            Coming Soon
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground hover:text-foreground px-3"
          asChild
        >
          <a href={app.githubUrl} target="_blank" rel="noopener noreferrer">
            <Github size={15} />
          </a>
        </Button>
      </div>
    </motion.div>
  );
};

const AppShowcaseSection = () => {
  return (
    <section id="apps" className="relative py-24 px-6 md:px-12 lg:px-24 overflow-hidden">
      {/* Background */}
      <div
        className="absolute inset-0 bg-grid opacity-20 pointer-events-none"
      />
      <div
        className="absolute left-1/2 top-0 -translate-x-1/2 w-[600px] h-[300px] pointer-events-none"
        style={{ background: 'radial-gradient(ellipse, hsl(var(--primary) / 0.03) 0%, transparent 60%)' }}
      />
       {/* Grid dots */}
      <div className="absolute inset-0 bg-grid opacity-40 pointer-events-none" />

      {/* Particles */}
      <div className="absolute inset-0">
        <ParticleField />
      </div>
      <div className="relative z-10 max-w-7xl mx-auto">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mb-16 "
        >
          <Badge variant="outline" className="border-primary/30 text-primary bg-primary/5 mb-4 font-mono text-xs uppercase tracking-wider">
            Live Apps
          </Badge>
          <h2 className="font-heading font-bold text-4xl md:text-5xl text-foreground leading-tight">
            Try My
            <span className="gradient-text"> Applications</span>
          </h2>
          <p className="text-muted-foreground mt-4 max-w-xl ">
            Real apps you can interact with right now — from AI voice authentication to LaTeX resume building.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {appShowcase.map((app, i) => (
            <AppCard key={app.id} app={app} index={i} />
          ))}
        </div>

        {/* CTA banner */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mt-12 glass rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6"
        >
          <div>
            <h3 className="font-heading font-bold text-foreground text-xl mb-2">More on GitHub</h3>
            <p className="text-muted-foreground text-sm">Explore all repositories, contributions, and open-source work.</p>
          </div>
          <Button
            variant="outline"
            size="lg"
            className="border-primary/30 text-primary hover:text-foreground hover:bg-primary/10 hover:border-primary/60 flex-shrink-0"
            asChild
          >
            <a href="https://github.com/bboyrajib" target="_blank" rel="noopener noreferrer">
              <Github size={18} className="mr-2" />
              View GitHub Profile
            </a>
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

export default AppShowcaseSection;
